/**
 * Decide which plan lines get checkboxes.
 * Only real student tasks — not headings, tips, links, or labels.
 */
export function isTrackablePlanLine(line) {
  const raw = String(line || "");
  const stripped = raw.replace(/^[▶•\-–—*▸▪☐☑\s]+/, "").trim();
  if (stripped.length < 4) return false;

  const isBullet = /^\s*[▶☐☑]/.test(raw);
  const isIndentedTask = /^\s{2,}Task\s*:/i.test(raw);
  const isNumbered = /^\s*\d+\)/.test(stripped);
  if (!isBullet && !isIndentedTask && !isNumbered) return false;

  // Meta / headings / labels — never checkbox
  if (
    /^(heading|reference links?|reference|video|website|youtube|type|category|tip|subject(?: area)?|next|focus|study tonight|practice focus|tonight'?s focus|overview|explain|how to use|topic:|learn:|do:|build:|problem(?: statement)?(?: \(in short\))?|dt playbook|what to learn|real project task|discussion prompt|how it helps|how it links|quick check|parts to cover|one topic today|what you need next|example(?: \(your problem\))?|motivation|done when|in short|class time|optional|follow-up|tonight|after class|log:|language|theme|purpose|target|rule|faculty note|worked example|check question|pipeline goal|connectivity|sns agent|tech stack|about this topic|today(?:'s|’s) focus|key points|sample data|product:|end with|use today's|finish checklist|your interests|your goals|domain you chose|today's learning topic|connect to|bonus check|class day ends|each person|write 3 lines:|personal check-in|team check-in|from your|extra:|same topic:|one more|answer the check|how to play|time:|quick game)\b/i.test(
      stripped
    )
  ) {
    return false;
  }

  // Pure link / reference detail lines
  if (/^(https?:\/\/|·\s*(website|youtube|practice|pattern|extra|video))/i.test(stripped)) {
    return false;
  }

  // Activity title-only lines (first line of a block)
  if (
    /^(stand-?up|learning|homework|tonight'?s homework|break|lunch|speak\s*&\s*solve|project build|placement prep|mini build|interests|goals|capability|domain|problem review|problem lab|coding practice|system design|agent workbench|refresh game|retrospective|peer review)\s*$/i.test(
      stripped
    )
  ) {
    return false;
  }

  // Bulletin / instruction lines that start with soft verbs but are not tasks
  if (/^(when you have|tick each|no fixed clock|tick each part)/i.test(stripped)) return false;

  // Numbered homework / learning completion items
  if (/^\d+\)/.test(stripped)) return true;
  // Homework task titles
  if (/^(leetcode|exercism|linkedin|agent workbench|case study)\b/i.test(stripped)) return true;
  if (/^complete(\s+topic|\s*:)?\b/i.test(stripped)) return true;
  if (/^work the example\b/i.test(stripped)) return true;
  if (/^answer the quick check\b/i.test(stripped)) return true;

  // Explicit task markers
  if (/^(task|step\s+\d|part\s+[a-z0-9])/i.test(stripped)) return true;
  if (/^do\b/i.test(stripped) && !/^done when/i.test(stripped)) return true;

  // Action lines students can verify done/not done
  if (
    /^(write|solve|finish|create|open|save|run|pick|list|rewrite|read|speak|post|connect|practice|implement|draw|underline|highlight|explain|answer|complete|review|add|build|draft|find|name|re-?read|revisit|say |keep |mark |star |present|document|capture|update|send|comment|share|comment on|try )\b/i.test(
      stripped
    )
  ) {
    return true;
  }

  // Retro connectivity answers
  if (/^\d+\)\s*(learned|finished|linked|tomorrow)\b/i.test(stripped)) return true;
  if (/^(learned|finished|linked|tomorrow)\b/i.test(stripped)) return true;

  return false;
}

/** Parse plan content into lines; only actionable tasks are trackable. */
export function parsePlanTaskLines(content) {
  const rawLines = String(content || "").split("\n");
  let subIndex = -1;
  return rawLines
    .filter((line) => {
      const stripped = line.replace(/^[\s▶•\-*☐☑]+/, "").trim();
      // Hide schedule noise (legacy plans may still contain these)
      if (/^week tracker\b/i.test(stripped)) return false;
      if (/^chapter tracker\b/i.test(stripped)) return false;
      if (/^tip:\s*/i.test(stripped)) return false;
      return true;
    })
    .map((line) => {
      const stripped = line.replace(/^[\s▶•\-*☐☑]+/, "").trim();
      const trackable = isTrackablePlanLine(line);
      if (trackable) {
        subIndex += 1;
        return { text: line, stripped, subIndex, trackable: true };
      }
      return { text: line, stripped, subIndex: null, trackable: false };
    });
}
